import { prettifyNumber } from 'helpers';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownItemProps } from 'semantic-ui-react';
import { sortBy, assoc, propEq } from 'ramda';

interface IProps {
  className?: string;
  creatable?: boolean;
  disabled?: boolean;
  icon?: string;
  inputType?: string;
  minCharacters?: number;
  noResultsMessage?: string;
  placeholder?: string;
  prefix?: string;
  search?: boolean;
  name: string;
  options: DropdownItemProps[];
  value: boolean | string | number | any[];
  onChange(event: SyntheticEvent, data: object): void;
}

const checkIsNewOption = (
  options: DropdownItemProps[],
  value: string | number,
) => !options.find(propEq('value', value));

const addOption = (
  options: DropdownItemProps[],
  prefix: string,
  value: string | number,
  inputType: string,
) => {
  if (value && checkIsNewOption(options, value)) {
    const text = inputType === 'number' ? prettifyNumber(value) : value;
    const newOptions = [
      { key: value, text: `${prefix}${text}`, value },
      ...options,
    ];
    return sortBy(propEq('value'), newOptions);
  }
  return options;
};

const getValue = (value: any, inputType: string) => {
  if (inputType === 'number') {
    const stripped =
      typeof value === 'string' ? value.replace(/[^0-9.]/g, '') : value;
    const parsed = Number(stripped || value);
    return Number.isNaN(parsed) ? '' : parsed;
  }
  return value;
};

const DropdownInput = ({
  className,
  disabled,
  placeholder,
  name,
  options: propOptions = [],
  prefix = '',
  icon,
  value,
  inputType = 'text',
  noResultsMessage,
  minCharacters,
  creatable,
  search = true,
  onChange,
}: IProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [stateOptions, setOptions] = useState<DropdownItemProps[]>(
    addOption(propOptions, prefix, value, inputType),
  );

  const options = useMemo(
    () => (creatable ? stateOptions : propOptions),
    [creatable, propOptions, stateOptions],
  );

  const handleAddition = useCallback(
    (_, data) => {
      setOptions((prevState) => {
        const val = getValue(data.value, inputType);
        return addOption(prevState, prefix, val, inputType);
      });
    },
    [inputType, prefix],
  );

  const handleChange = useCallback(
    (e, data) => {
      const val = getValue(data.value, inputType);
      onChange(e, assoc('value', val, data));
    },
    [inputType, onChange],
  );

  const handleClose = useCallback((e) => {
    setTimeout(() => {
      // Blur search input to close dropdown
      e?.target?.parentNode?.parentNode?.firstChild?.blur?.();
    }, 10);
  }, []);

  return (
    <Dropdown
      ref={ref}
      additionLabel={prefix}
      search={search}
      selection={true}
      allowAdditions={creatable}
      className={className}
      disabled={disabled}
      options={options}
      name={name}
      value={value}
      placeholder={placeholder}
      icon={icon}
      closeOnChange={true}
      onAddItem={handleAddition}
      onChange={handleChange}
      onClose={handleClose}
      noResultsMessage={noResultsMessage}
      minCharacters={minCharacters}
    />
  );
};

export default DropdownInput;
